/**
 * WHAT MAY BE QUEUED OFFLINE — and, more importantly, what may not.
 *
 * ---------------------------------------------------------------------------
 * WHY A DENY LIST EXISTS AT ALL
 * ---------------------------------------------------------------------------
 * An offline outbox is a promise to the user: "tap it, it will happen". For a
 * dish being marked ready that promise is safe — the worst case is that the KDS
 * and the server disagree for ninety seconds. For anything that MOVES MONEY or
 * CHANGES AUTHORITY the promise is a lie, because the client cannot see the
 * facts the decision depends on:
 *
 *   - REFUNDS: the client cannot know how much of the payment has already been
 *     refunded by another terminal. Queuing a refund offline is how you refund
 *     the same bill twice. The API also demands `x-action-reason` and a live
 *     `payment.refund` check; both are pointless if the check happens an hour
 *     late.
 *   - DISCOUNTS ABOVE THE LIMIT: the ceiling lives in a branch setting
 *     (`order.discount.max_bp`) and the override needs a second permission
 *     (`order.discount.override`) which a manager may have revoked while we
 *     were offline. An offline client would be granting itself authority.
 *   - PERMISSION / ROLE CHANGES: the whole point of a permission change is that
 *     it takes effect NOW. Replaying one from an outbox re-grants access that a
 *     manager may have deliberately removed minutes ago — a security defect,
 *     not a sync defect.
 *
 * Everything not on the allow list below is rejected by `assertQueueable()`,
 * which THROWS. It throws rather than returning false so that a future screen
 * cannot quietly opt a forbidden action into the queue by ignoring a boolean.
 *
 * The allow list is deliberately identical to the permissions flagged
 * `offlineAllowed` in `@sonauto/permissions/catalog.ts`. It is restated here as
 * data (rather than imported) so this package stays dependency-light and so the
 * two lists can be diffed by the test suite instead of silently co-varying.
 */

import type { OutboxKind } from './types.js';

/**
 * Operation kinds the outbox accepts, each with the permission that authorises
 * it. Every permission here carries `offlineAllowed: true` in the catalogue.
 */
export const OFFLINE_ALLOWED: Readonly<Record<OutboxKind, string>> = {
  ORDER_CREATE: 'order.create',
  ORDER_CONFIRM: 'order.confirm',
  TABLE_OPEN: 'table.open',
  PRODUCTION_ACCEPT: 'kitchen.accept',
  PRODUCTION_START: 'kitchen.start',
  PRODUCTION_COMPLETE: 'kitchen.complete',
  PRODUCTION_PICKUP: 'task.accept',
  PRODUCTION_DELIVER: 'task.complete',
  TASK_ACCEPT: 'task.accept',
  TASK_COMPLETE: 'task.complete',
  ATTENDANCE_CHECK_IN: 'attendance.checkin',
  ATTENDANCE_CHECK_OUT: 'attendance.checkout',
  PRODUCT_AVAILABILITY: 'product.availability.manage',
  // Both are `offlineAllowed: true` in the catalogue, and the API answers a
  // replayed completion with `{ alreadyDone: true }` instead of 409 — see
  // apps/api-worker/src/routes/tables-ops.ts.
  TABLE_CLEAN: 'table.clean',
  TABLE_SETUP: 'table.setup',
};

/**
 * Permissions that must NEVER reach the outbox, with the reason a reviewer
 * needs. Exported so the UI can explain the refusal instead of just greying a
 * button out.
 */
export const NEVER_QUEUE_OFFLINE: Readonly<Record<string, string>> = {
  'payment.refund':
    'Hoàn tiền cần kiểm tra số đã hoàn trên máy chủ; xếp hàng offline có thể hoàn trùng.',
  'order.refund':
    'Hoàn tiền đơn hàng cần kiểm tra số đã hoàn trên máy chủ; xếp hàng offline có thể hoàn trùng.',
  'order.discount':
    'Giảm giá phải được kiểm tra với hạn mức của chi nhánh tại thời điểm thực hiện.',
  'order.discount.override':
    'Giảm giá vượt hạn mức cần quyền duyệt còn hiệu lực ngay lúc đó, không thể duyệt lùi.',
  'order.gift_item': 'Tặng món là thao tác nhạy cảm, phải ghi nhận người duyệt tại thời điểm thực hiện.',
  'order.cancel': 'Hủy đơn cần kiểm tra tiền đã thu trên máy chủ.',
  'order.item.cancel': 'Hủy món cần kiểm tra trạng thái chế biến thật trên máy chủ.',
  'payment.receive': 'Thanh toán phải đối chiếu số dư còn lại trên máy chủ, không thu offline.',
  'payment.credit': 'Ghi công nợ cần hạn mức công nợ hiện hành của khách.',
  'invoice.reprint': 'In lại hóa đơn là thao tác chống gian lận, phải ghi nhận trực tuyến.',
  'cash.movement.create': 'Thu chi tiền mặt phải gắn với ca quỹ đang mở trên máy chủ.',
  'employee.role.assign': 'Thay đổi phân quyền phải có hiệu lực ngay, không được phát lại từ hàng đợi.',
  'employee.manage': 'Thay đổi nhân sự phải có hiệu lực ngay, không được phát lại từ hàng đợi.',
  'device.manage': 'Quản lý thiết bị ảnh hưởng bảo mật, phải thực hiện trực tuyến.',
  'settings.manage': 'Cấu hình hệ thống phải có hiệu lực ngay.',
  'table.transfer': 'Chuyển bàn cần trạng thái bàn đích tại thời điểm thực hiện.',
  'table.merge': 'Ghép bàn cần trạng thái hai phiên bàn tại thời điểm thực hiện.',
  'table.split': 'Tách bàn cần trạng thái phiên bàn tại thời điểm thực hiện.',
};

const ALLOWED_KINDS: ReadonlySet<string> = new Set(Object.keys(OFFLINE_ALLOWED));

/** Is this operation kind allowed to sit in the outbox? */
export function isQueueableOffline(kind: string): kind is OutboxKind {
  return ALLOWED_KINDS.has(kind);
}

/** Is this permission code forbidden from ever being exercised offline? */
export function isForbiddenOffline(permissionCode: string): boolean {
  return Object.prototype.hasOwnProperty.call(NEVER_QUEUE_OFFLINE, permissionCode);
}

/** Vietnamese explanation for a refusal, or `null` when the code is fine. */
export function offlineRefusalReason(permissionCode: string): string | null {
  return NEVER_QUEUE_OFFLINE[permissionCode] ?? null;
}

/**
 * Enforcement point. Throws — deliberately — so a forbidden operation cannot be
 * queued by a caller that ignores a return value.
 */
export function assertQueueable(kind: string): asserts kind is OutboxKind {
  if (!isQueueableOffline(kind)) {
    throw new Error(
      `offline-sync: thao tác "${kind}" không được phép xếp hàng offline. ` +
        'Chỉ các thao tác trong OFFLINE_ALLOWED mới được đưa vào hàng đợi.',
    );
  }
}
